import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ResourcesConfigEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function ResourcesConfigEditor({ flow, canEdit, onSave }: ResourcesConfigEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)

  // Edit state
  const [editedLimitsCpu, setEditedLimitsCpu] = useState('')
  const [editedLimitsMemory, setEditedLimitsMemory] = useState('')
  const [editedRequestsCpu, setEditedRequestsCpu] = useState('')
  const [editedRequestsMemory, setEditedRequestsMemory] = useState('')

  const handleEdit = () => {
    // Initialize edit state with current values
    setEditedLimitsCpu(flow.spec?.resources?.limits?.cpu || '')
    setEditedLimitsMemory(flow.spec?.resources?.limits?.memory || '')
    setEditedRequestsCpu(flow.spec?.resources?.requests?.cpu || '')
    setEditedRequestsMemory(flow.spec?.resources?.requests?.memory || '')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSave = () => {
    // Build resources object
    const limits: any = {}
    const requests: any = {}

    if (editedLimitsCpu.trim()) limits.cpu = editedLimitsCpu.trim()
    if (editedLimitsMemory.trim()) limits.memory = editedLimitsMemory.trim()
    if (editedRequestsCpu.trim()) requests.cpu = editedRequestsCpu.trim()
    if (editedRequestsMemory.trim()) requests.memory = editedRequestsMemory.trim()

    const resources: any = {}
    if (Object.keys(limits).length > 0) resources.limits = limits
    if (Object.keys(requests).length > 0) resources.requests = requests

    // Create updated flow object
    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        resources: Object.keys(resources).length > 0 ? resources : undefined,
      }
    }

    // Remove undefined fields
    if (!updatedFlow.spec.resources) {
      delete updatedFlow.spec.resources
    }

    onSave(updatedFlow, t('detail.resourcesConfig'))
    setIsEditing(false)
  }

  const hasResources = flow.spec?.resources && (
    (flow.spec.resources.limits && Object.keys(flow.spec.resources.limits).length > 0) ||
    (flow.spec.resources.requests && Object.keys(flow.spec.resources.requests).length > 0)
  )

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.resourcesConfig', 'Resources Configuration')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div>
          <div className="grid grid-cols-2 gap-6">
            {/* Limits */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.resourcesLimits', 'Limits')}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">CPU</label>
                  <input
                    type="text"
                    value={editedLimitsCpu}
                    onChange={(e) => setEditedLimitsCpu(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                    placeholder="1000m or 1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">{t('detail.resourcesMemory', 'Memory')}</label>
                  <input
                    type="text"
                    value={editedLimitsMemory}
                    onChange={(e) => setEditedLimitsMemory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                    placeholder="512Mi or 1Gi"
                  />
                </div>
              </div>
            </div>

            {/* Requests */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.resourcesRequests', 'Requests')}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">CPU</label>
                  <input
                    type="text"
                    value={editedRequestsCpu}
                    onChange={(e) => setEditedRequestsCpu(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                    placeholder="500m or 0.5"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">{t('detail.resourcesMemory', 'Memory')}</label>
                  <input
                    type="text"
                    value={editedRequestsMemory}
                    onChange={(e) => setEditedRequestsMemory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                    placeholder="256Mi or 512Mi"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-6">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* Limits */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.resourcesLimits', 'Limits')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <dt className="text-sm font-medium text-gray-500">CPU</dt>
                <dd className="text-sm text-gray-900 font-mono">{flow.spec?.resources?.limits?.cpu || '-'}</dd>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <dt className="text-sm font-medium text-gray-500">{t('detail.resourcesMemory', 'Memory')}</dt>
                <dd className="text-sm text-gray-900 font-mono">{flow.spec?.resources?.limits?.memory || '-'}</dd>
              </div>
            </dl>
          </div>

          {/* Requests */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('detail.resourcesRequests', 'Requests')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <dt className="text-sm font-medium text-gray-500">CPU</dt>
                <dd className="text-sm text-gray-900 font-mono">{flow.spec?.resources?.requests?.cpu || '-'}</dd>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <dt className="text-sm font-medium text-gray-500">{t('detail.resourcesMemory', 'Memory')}</dt>
                <dd className="text-sm text-gray-900 font-mono">{flow.spec?.resources?.requests?.memory || '-'}</dd>
              </div>
            </dl>
          </div>

          {!hasResources && (
            <div className="col-span-2 text-center text-sm text-gray-500 py-4">
              {t('detail.noResourcesConfigured', 'No resource limits or requests configured')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
